import { useEffect, useRef } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { ArrowLeft, ArrowUpRight, MessageCircle, Facebook, Link2 } from "lucide-react";
import { toast } from "sonner";
import { PageShell } from "../components/PageShell";
import { MaskLines, Reveal } from "../components/Reveal";
import { formatDate } from "../lib/blog";
import { usePosts, useContentLoaded } from "../lib/content";
import { WHATSAPP } from "../lib/i18n";
import { TREATMENTS } from "../lib/treatments";
import { PostCard } from "./Blog";

const Block = ({ type, text, first }) => {
  if (type === "h") return <h2 className="mt-14 font-serif-d font-light text-3xl sm:text-4xl text-[#14110F]">{text}</h2>;
  if (type === "q") {
    return (
      <blockquote className="my-14 border-l-2 border-[#C9A66B] pl-6 sm:pl-10 font-serif-d italic font-light text-3xl sm:text-4xl leading-[1.2] text-[#8A6A3A]">
        “{text}”
      </blockquote>
    );
  }
  return (
    <p className={`mt-6 text-[1.05rem] sm:text-lg leading-[1.85] text-[#3d3731] font-light ${first ? "first-letter:font-serif-d first-letter:text-7xl first-letter:float-left first-letter:mr-3 first-letter:mt-1 first-letter:leading-[0.8] first-letter:text-[#8A6A3A]" : ""}`}>
      {text}
    </p>
  );
};

const ShareBar = ({ post }) => {
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/share/blog/${post.slug}`;
  const text = `${post.title} — dal blog di Gioia Centro Estetico`;
  const btn = "w-11 h-11 rounded-full border border-[#ddd4c7] flex items-center justify-center text-[#14110F] transition-colors duration-300 hover:bg-[#14110F] hover:text-[#F6F2EC] hover:border-[#14110F]";
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copiato"); } catch { toast.error("Copia non riuscita"); }
  };
  return (
    <div className="mt-16 pt-8 border-t border-[#ddd4c7] flex items-center justify-between gap-4" data-testid="post-share">
      <p className="overline text-[0.62rem] text-[#5a534c]">Condividi con un'amica</p>
      <div className="flex gap-3">
        <a href={`https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`} target="_blank" rel="noreferrer" aria-label="WhatsApp" data-testid="post-share-whatsapp" className={btn}><MessageCircle className="w-4 h-4" /></a>
        <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`} target="_blank" rel="noreferrer" aria-label="Facebook" data-testid="post-share-facebook" className={btn}><Facebook className="w-4 h-4" /></a>
        <button type="button" onClick={copy} aria-label="Copia link" data-testid="post-share-copy" className={btn}><Link2 className="w-4 h-4" /></button>
      </div>
    </div>
  );
};

export default function BlogPost() {
  const { slug } = useParams();
  const posts = usePosts();
  const loaded = useContentLoaded();
  const post = posts.find((p) => p.slug === slug);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 24 });
  const { scrollYProgress: coverP } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(coverP, [0, 1], ["-6%", "6%"]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (post) document.title = `${post.title} · Gioia Centro Estetico`;
  }, [post]);

  if (!post) return loaded ? <Navigate to="/blog" replace /> : null;
  const related = posts.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <PageShell>
      <motion.div style={{ scaleX: progress }} className="fixed top-0 left-0 right-0 h-[2px] bg-[#C9A66B] origin-left z-[70]" data-testid="reading-progress" />
      <article className="max-w-[1440px] mx-auto" data-testid="blog-post-page">
        <div className="max-w-4xl mx-auto text-center">
          <Link to="/blog" data-testid="post-back-link" className="inline-flex items-center gap-2 overline text-[0.62rem] text-[#5a534c] hover:text-[#8A6A3A] transition-colors duration-300">
            <ArrowLeft className="w-3.5 h-3.5" /> Journal
          </Link>
          <p className="mt-8 overline text-[0.62rem] text-[#8A6A3A]">{post.category} · {formatDate(post.date)} · {post.read} min</p>
          <h1 className="mt-6 font-serif-d font-light text-[2.6rem] sm:text-6xl lg:text-7xl leading-[1] text-[#14110F]" data-testid="post-title">
            <MaskLines key={post.slug} lines={[post.title]} delay={0.1} />
          </h1>
          <Reveal delay={0.2}><p className="mt-8 text-lg text-[#4f4841] font-light leading-relaxed max-w-2xl mx-auto">{post.excerpt}</p></Reveal>
        </div>

        <motion.div
          ref={ref}
          initial={{ clipPath: "inset(12% 12% 12% 12%)" }}
          animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
          transition={{ duration: 1.5, delay: 0.3, ease: [0.76, 0, 0.24, 1] }}
          className="relative mt-14 sm:mt-20 overflow-hidden aspect-[4/5] sm:aspect-[16/8] bg-[#14110F]"
          data-testid="post-cover"
        >
          <motion.img style={{ y, scale: 1.14, objectPosition: post.pos }} src={post.cover} alt={post.title} className="absolute inset-0 w-full h-full object-cover" />
        </motion.div>

        <div className="max-w-2xl mx-auto mt-16 sm:mt-24" data-testid="post-body">
          {post.body.map(([type, text], i) => <Block key={i} type={type} text={text} first={i === 0} />)}

          <ShareBar post={post} />
          {TREATMENTS[post.treatment] && (
            <Link to={`/trattamenti/${post.treatment}`} data-testid="post-treatment-link" className="group mt-16 flex items-center justify-between gap-6 border border-[#ddd4c7] p-6 sm:p-8 transition-colors duration-300 hover:border-[#8A6A3A]">
              <span>
                <span className="overline text-[0.62rem] text-[#8A6A3A]">Scopri il trattamento</span>
                <span className="block mt-3 font-serif-d font-light text-3xl sm:text-4xl text-[#14110F] leading-tight">{TREATMENTS[post.treatment].it.title}</span>
                <span className="block mt-2 text-sm text-[#4f4841]">Come funziona, per chi è e le domande frequenti</span>
              </span>
              <span className="shrink-0 w-12 h-12 rounded-full bg-[#14110F] text-[#F6F2EC] flex items-center justify-center transition-colors duration-300 group-hover:bg-[#8A6A3A]">
                <ArrowUpRight className="w-5 h-5 transition-transform duration-500 group-hover:rotate-45" />
              </span>
            </Link>
          )}
          <div className="mt-20 bg-[#14110F] text-[#F6F2EC] p-8 sm:p-12 grain relative overflow-hidden" data-testid="post-cta">
            <p className="overline text-[0.62rem] text-[#C9A66B]">Gioia Centro Estetico</p>
            <p className="mt-4 font-serif-d font-light text-3xl sm:text-4xl leading-tight">Vuoi un consiglio personalizzato?</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link to="/#booking" data-testid="post-book-btn" className="inline-flex justify-center rounded-full bg-[#C9A66B] text-[#14110F] px-7 py-3.5 text-[0.72rem] tracking-[0.2em] uppercase transition-colors duration-500 hover:bg-[#F6F2EC]">Prenota</Link>
              <a href={WHATSAPP} target="_blank" rel="noreferrer" data-testid="post-whatsapp-btn" className="inline-flex justify-center items-center gap-2 rounded-full border border-[#F6F2EC]/30 px-7 py-3.5 text-[0.72rem] tracking-[0.2em] uppercase transition-colors duration-500 hover:border-[#C9A66B] hover:text-[#C9A66B]">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            </div>
          </div>
        </div>

        <section className="mt-28 sm:mt-36 border-t border-[#ddd4c7] pt-14" data-testid="post-related">
          <h2 className="font-serif-d font-light text-4xl sm:text-5xl text-[#14110F]">Continua a leggere</h2>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
            {related.map((p, i) => <PostCard key={p.slug} p={p} i={i} />)}
          </div>
        </section>
      </article>
    </PageShell>
  );
}
